import { useEffect, useState } from "react";
import { getBadgeById, getUserBadgesByUserId } from "../services/badge";
import { getCourseByAssetTypeAndId, getCoursesAndFirstLessons, getVisibleCourseAssetIds } from "../services/course";
import { getAllCoursesByUserEnrollments } from "../services/enrollment";
import { RELATIONSHIP, RELATIONSHIP_FIELD, RELATIONSHIP_NAME } from "../utils/constants";
import { getCurrentUserId, splitStringtoArray } from "../utils/util";

const useFetchUserEnrollments = () => {
  const [userBadges, setUsersBadges] = useState([]);
  const [userCompleteEnrollments, setUserCompleteEnrollments] = useState([]);
  const [userInProgressEnrollments, setUserInProgressEnrollments] = useState([]);

  useEffect(() => {
    const fetchUserBadges = async () => {
      const data = await getUserBadgesByUserId(getCurrentUserId());
      
      const userBadgesList = await Promise.all(
        data.items.map(async (item) => {
          try {
            const badgeCourse = await getCourseByAssetTypeAndId(
              RELATIONSHIP.MODULES_TO_QUIZZES,
              item.quizId
            );
            const userBadge = await getBadgeById(item.badgeId);

            return {
              course: badgeCourse,
              image: userBadge?.badge?.link?.href ?? '',
              title: userBadge?.title ?? '',
              year: userBadge?.year ?? '',
            };
          } catch (error) {
            console.warn(`Failed to load badge ${item.badgeId}`, error);
            return null;
          }
        })
      );

      const filteredUserBadgesList = userBadgesList.filter(
        (badge) => badge !== null
      );

      setUsersBadges(filteredUserBadgesList);

      return filteredUserBadgesList;
    };

    const fetchUserEnrollments = async (filteredUserBadgesList) => {
      const activeCourses = await getAllCoursesByUserEnrollments();
      const passedQuizCourses = filteredUserBadgesList
        .filter((item) => item.course)
        .map((item) => item.course);
      const data = await getCoursesAndFirstLessons();

      const coursesFirstLessonList = data
        .filter((course) => course[RELATIONSHIP.MODULES_TO_LESSONS] && course[RELATIONSHIP_NAME.COURSES_IN_MODULES])
        .map((course) => ({
          [course[RELATIONSHIP_NAME.COURSES_IN_MODULES].id]: course[RELATIONSHIP.MODULES_TO_LESSONS][0],
        }));

      const findEnrolledCourseFirstLesson = (
        courseId,
        coursesFirstLessonList
      ) => {
        const courseFirstLesson = coursesFirstLessonList.find(
          (obj) => Object.keys(obj)[0] === courseId.toString()
        );

        return courseFirstLesson ? Object.values(courseFirstLesson)[0] : null;
      };

      const userEnrolledCourseList = await Promise.all(
        activeCourses.items.map(async (item) => {
          const courseId = item[RELATIONSHIP_FIELD.COURSES_IN_ENROLLMENTS];

          if (!item[RELATIONSHIP_NAME.COURSES_IN_ENROLLMENTS]) {
            return null;
          }

          const firstLesson = findEnrolledCourseFirstLesson(
            courseId,
            coursesFirstLessonList
          );

          const completedAssets = splitStringtoArray(item.completedAssetIds);

          let visibleAssetIds = null;

          try {
            visibleAssetIds = await getVisibleCourseAssetIds(courseId);
          } catch (error) {
            console.warn(
              `Failed to load visible assets for course ${courseId}`,
              error
            );
          }

          let courseProgress;
          let isCompletedCourseByAssets;

          if (visibleAssetIds && visibleAssetIds?.length) {
            const completedAssetsSet = new Set(completedAssets);
            const completedVisibleAssets = visibleAssetIds.filter((assetId) =>
              completedAssetsSet.has(assetId)
            ).length;

            isCompletedCourseByAssets =
              completedVisibleAssets >= visibleAssetIds.length;
            courseProgress = parseInt(
              ((completedVisibleAssets / visibleAssetIds.length) * 100).toFixed(0)
            );
          } else {
            isCompletedCourseByAssets =
              completedAssets.length >=
                item[RELATIONSHIP_NAME.COURSES_IN_ENROLLMENTS].totalAssets;
            courseProgress =
              completedAssets[0] === 0
                ? 0
                : parseInt(
                  (
                    (completedAssets.length /
                      item[RELATIONSHIP_NAME.COURSES_IN_ENROLLMENTS].totalAssets) *
                      100
                  ).toFixed(0)
                );
          }

          const isCompletedCourseByQuiz = passedQuizCourses.includes(courseId);

          const isCourseComplete =
            isCompletedCourseByAssets || isCompletedCourseByQuiz;

          return {
            courseComplete: isCourseComplete,
            courseProgress: courseProgress,
            description: item[RELATIONSHIP_NAME.COURSES_IN_ENROLLMENTS].description,
            firstLesson: firstLesson?.id,
            firstLessonFriendlyUrlPath: firstLesson?.friendlyUrlPath,
            id: courseId,
            title: item[RELATIONSHIP_NAME.COURSES_IN_ENROLLMENTS].title,
          };
        })
      );

      const validEnrolledCourseList = userEnrolledCourseList.filter(
        (item) => item !== null
      );

      setUserCompleteEnrollments(
        validEnrolledCourseList.filter((item) => item.courseComplete)
      );
      setUserInProgressEnrollments(
        validEnrolledCourseList.filter((item) => !item.courseComplete)
      );
    };

    const fetchData = async () => {
      let badges = [];
      try {
        badges = await fetchUserBadges();
      } catch (error) {
        console.error('fetchUserBadges failed', error);
      }
      try {
        await fetchUserEnrollments(badges);
      } catch (error) {
        console.error('fetchUserEnrollments failed', error);
      }
    };

    fetchData();
  }, [setUserCompleteEnrollments, setUserInProgressEnrollments, setUsersBadges]);

  return (
    {userBadges, userCompleteEnrollments, userInProgressEnrollments}
  )
};
export default useFetchUserEnrollments;